import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { searchHotels, selectHotel, fetchRoomRates } from '../api/functions';
import { getCityName } from '../components/airportUtils';
import { cityCoordinates } from '../components/cityCoordinates';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, MapPin, ArrowLeft, SlidersHorizontal, Search, ChevronLeft, ChevronRight, Map as MapIcon, Star, Hotel } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import StayOfferCard from '../components/search/StayOfferCard';
import RoomRatesModal from '../components/stays/RoomRatesModal';
import { resolveHotelRating, compareHotelsByRating } from '../utils/hotelRating';
import DarkBasemap from '../components/maps/DarkBasemap';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import { isSeatedInfant } from '@/utils/passengerTypes';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function Stays() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [allHotels, setAllHotels] = useState([]); // Store all hotels
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [sortBy, setSortBy] = useState('price_high'); // Default: Price (High to Low)
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const [currentPage, setCurrentPage] = useState(1); // Pagination
  const itemsPerPage = 100; // Show 100 hotels per page
  const [showMapView, setShowMapView] = useState(false); // Mobile map toggle
  const [cityCenter, setCityCenter] = useState(null); // Store city center coordinates
  const [showRoomRatesModal, setShowRoomRatesModal] = useState(false); // Room rates modal for map popup
  const [popupHotelWithRates, setPopupHotelWithRates] = useState(null); // Hotel with rates for popup
  const hotelListRef = useRef(null);
  const [flightData, setFlightData] = useState(null); // Store flight info for pricing

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const { selectedFlight, returnFlight, bookingOption, searchParams: params } = location.state || {};
        
        // Store flight data for pricing calculations (include bookingOption for correct fare price)
        if (selectedFlight) {
          setFlightData({ selectedFlight, returnFlight, bookingOption });
        }

        if (!selectedFlight || !params) {
          toast.error('Missing flight information');
          navigate('/search');
          return;
        }

        setSearchParams(params);

        console.log('FlightOptions passed searchParams:', params);

        // Extract destination city from selected flight
        // Try multiple possible data structures for destination
        const lastSegment = selectedFlight.slices?.[0]?.segments?.[
          selectedFlight.slices[0].segments.length - 1
        ];
        
        const destinationAirport = lastSegment?.destination || lastSegment?.arrival_airport;

        if (!destinationAirport) {
          console.error('Could not find destination in flight data:', selectedFlight);
          toast.error('Could not determine destination from flight selection');
          navigate('/search');
          return;
        }

        // Extract check-in and check-out dates from ACTUAL SELECTED FLIGHTS
        // This ensures hotel dates match the flights the user chose, not the original search dates
        let checkInDate = null;
        let checkOutDate = null;
        
        // Priority 1: Extract from actual selected flight arrival date
        // Outbound arrival date = hotel check-in
        const outboundSegments = selectedFlight?.slices?.[0]?.segments;
        if (outboundSegments && outboundSegments.length > 0) {
          const lastOutboundSegment = outboundSegments[outboundSegments.length - 1];
          const arrivalDateTime = lastOutboundSegment?.arriving_at || lastOutboundSegment?.arrival_time;
          if (arrivalDateTime) {
            // Extract just the date part (YYYY-MM-DD)
            checkInDate = arrivalDateTime.split(' ')[0].split('T')[0];
            console.log('Hotel check-in extracted from outbound arrival:', checkInDate);
          }
        }
        
        // Priority 2: Extract from return flight departure date = hotel check-out
        const returnSegments = returnFlight?.slices?.[0]?.segments;
        if (returnSegments && returnSegments.length > 0) {
          const firstReturnSegment = returnSegments[0];
          const departureDateTime = firstReturnSegment?.departing_at || firstReturnSegment?.departure_time;
          if (departureDateTime) {
            checkOutDate = departureDateTime.split(' ')[0].split('T')[0];
            console.log('Hotel check-out extracted from return departure:', checkOutDate);
          }
        }
        
        // Fallback: Use search params if flight dates couldn't be extracted
        if (!checkInDate) {
          checkInDate = params.departureDate;
          if (!checkInDate && params.slices && params.slices.length > 0) {
            checkInDate = params.slices[0].departure_date;
          }
          console.log('Hotel check-in fallback to search params:', checkInDate);
        }
        if (!checkOutDate) {
          checkOutDate = params.returnDate;
          if (!checkOutDate && params.slices && params.slices.length > 1) {
            checkOutDate = params.slices[1].departure_date;
          }
          console.log('Hotel check-out fallback to search params:', checkOutDate);
        }

        if (!checkInDate || !checkOutDate) {
          console.error('Missing travel dates. searchParams:', params);
          toast.error('Missing travel dates');
          navigate('/search');
          return;
        }

        // Validate that check-out is after check-in
        const checkIn = new Date(checkInDate);
        const checkOut = new Date(checkOutDate);
        
        if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
          console.error('Invalid date format:', { checkInDate, checkOutDate });
          toast.error('Invalid date format');
          navigate('/search');
          return;
        }
        
        // If check-out is same as or before check-in, add 1 day to check-out
        if (checkOut <= checkIn) {
          console.warn('Check-out date is not after check-in, adjusting check-out date');
          const adjustedCheckOut = new Date(checkIn);
          adjustedCheckOut.setDate(adjustedCheckOut.getDate() + 1);
          checkOutDate = adjustedCheckOut.toISOString().split('T')[0];
          console.log('Adjusted check-out date to:', checkOutDate);
        }

        // Get destination IATA code
        const destinationCode = destinationAirport.iata_code || destinationAirport.id;
        
        // Get city name from airport code
        const cityName = getCityName(destinationCode);
        
        // Get city center coordinates (not airport coordinates)
        const cityCoords = cityCoordinates[cityName];
        
        if (!cityCoords || !Array.isArray(cityCoords) || cityCoords.length !== 2) {
          console.error(`No city center coordinates found for ${cityName} (${destinationCode})`);
          toast.error(`Hotels not available for ${cityName} at this time`);
          navigate('/search');
          return;
        }

        const coordinates = {
          lat: cityCoords[0],
          lng: cityCoords[1]
        };
        
        // Save city center for map
        setCityCenter(coordinates);
        
        console.log(`Using city center coordinates for ${cityName} (${destinationCode}):`, coordinates);

        console.log('Hotel search params:', {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          checkInDate,
          checkOutDate,
        });

        // Build search params for Duffel API (uses lat/lng instead of destination)
        const searchQueryParams = {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          radius: 15, // 15 km search radius
          check_in_date: checkInDate,
          check_out_date: checkOutDate,
          rooms: 1,
          adults: params.passengers?.filter(p => p.type === 'adult').length || 2,
          children: params.passengers?.filter(p => p.type === 'child' || isSeatedInfant(p.type)).length || 0,
          free_cancellation_only: false
        };

        console.log('Fetching hotels from Duffel API...');
        
        // Duffel API doesn't use pagination like SerpAPI, it returns all results at once
        const pageResults = await searchHotels(searchQueryParams);
        const allHotelsData = pageResults.data || [];

        console.log(`Hotel loading complete! Loaded ${allHotelsData.length} hotels from Duffel API`);

        // Deduplicate hotels by ID (use search_result_id as unique key)
        const uniqueHotelsMap = new Map();
        allHotelsData.forEach(hotel => {
          const hotelId = hotel.id || hotel.serpapi_hotel_id;
          if (hotelId && !uniqueHotelsMap.has(hotelId)) {
            uniqueHotelsMap.set(hotelId, hotel);
          }
        });
        
        const uniqueHotels = Array.from(uniqueHotelsMap.values());
        console.log(`After deduplication: ${uniqueHotels.length} unique hotels (removed ${allHotelsData.length - uniqueHotels.length} duplicates)`);

        // Set all hotels for sorting and display
        setAllHotels(uniqueHotels);
        // Sorting will be handled by the sorting useEffect with default "recommended"
      } catch (error) {
        console.error('Failed to fetch hotels:', error);
        toast.error('Failed to load hotel options');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotels();
  }, [location.state, navigate]);

  // Handle search filtering, sorting, and pagination
  useEffect(() => {
    if (allHotels.length === 0) {
      setHotels([]); // Clear hotels to show empty state
      return;
    }

    let filteredHotels = [...allHotels];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredHotels = filteredHotels.filter(hotel => {
        const name = hotel.name?.toLowerCase() ?? '';
        const address = hotel.address?.toLowerCase() ?? '';
        return name.includes(query) || address.includes(query);
      });
    }

    // Sort hotels based on selected option
    switch (sortBy) {
      case 'price_low':
        filteredHotels.sort((a, b) => (a.total_amount || 0) - (b.total_amount || 0));
        break;
      case 'price_high':
        filteredHotels.sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0));
        break;
      case 'rating':
        filteredHotels.sort(compareHotelsByRating);
        break;
      default:
        break;
    }

    // Paginate: Show only current page (100 hotels per page)
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedHotels = filteredHotels.slice(startIndex, endIndex);

    setHotels(paginatedHotels);
  }, [allHotels, sortBy, searchQuery, currentPage, itemsPerPage]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy]);

  const handleHotelSelect = (hotel, selectedRate) => {
    setSelectedHotel({ ...hotel, selectedRate });
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Calculate filtered hotels count and total pages
  const getFilteredHotelsCount = () => {
    if (allHotels.length === 0) return 0;
    
    let filtered = [...allHotels];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(hotel => {
        const name = hotel.name?.toLowerCase() ?? '';
        const address = hotel.address?.toLowerCase() ?? '';
        return name.includes(query) || address.includes(query);
      });
    }
    return filtered.length;
  };

  const filteredCount = getFilteredHotelsCount();
  const totalPages = Math.ceil(filteredCount / itemsPerPage);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContinue = async () => {
    if (!selectedHotel) {
      toast.error('Please select a hotel');
      return;
    }

    if (!selectedHotel.selectedRate) {
      toast.error('Please select a room type');
      return;
    }

    setIsSavingSelection(true);

    try {
      // Use session_id from FlightOptions (passed via location.state)
      const sessionId = location.state?.sessionId || `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      // Save hotel selection with room rate to database
      await selectHotel(selectedHotel, selectedHotel.selectedRate, sessionId);

      const { selectedFlight, returnFlight, bookingOption, paymentMethod, choseRefundableInstallments, deferaZero, milesBalance, reserveBalanceCents, reserveTripId } = location.state || {};

      // Navigate to Review page with combined package
      navigate('/Review', {
        state: {
          selectedFlight,
          returnFlight,
          bookingOption, // CRITICAL: Pass booking option so Review uses correct combined price
          searchParams,
          passengers: searchParams?.passengers || [],
          selectedHotel,
          packageType: 'flight_stay',
          isGoogleFlight: true,
          sessionId, // Pass session ID to Review page for abandoned trip
          paymentMethod,
          choseRefundableInstallments,
          deferaZero,
          milesBalance,
          reserveBalanceCents,
          reserveTripId
        },
      });
    } catch (error) {
      console.error('Failed to save hotel selection:', error);
      toast.error('Failed to save hotel selection. Please try again.');
    } finally {
      setIsSavingSelection(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Helper to get consistent hotel ID (fallback to serpapi_hotel_id if id doesn't exist)
  const getHotelId = (hotel) => {
    return hotel?.id || hotel?.serpapi_hotel_id;
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      const isWholeNumber = price % 1 === 0;
      const formatted = isWholeNumber ? price.toFixed(0) : price.toFixed(2);
      return `$${formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
    return price;
  };

  return (
    <div className="min-h-screen bg-black">
      <AnimatePresence>
        {/* Loading State */}
        {isLoading && (
          <motion.div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {/* Elegant background with dynamic glow */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Vibrant gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-950/30 via-black to-brand-950/30"></div>
              
              {/* Floating orbs - more visible */}
              <div className="orbs-container">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className={`floating-orb orb-${i + 1}`}></div>
                ))}
              </div>
              
              {/* Elegant rotating rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                {[...Array(3)].map((_, i) => (
                  <div 
                    key={i} 
                    className="search-ring"
                    style={{ 
                      animationDelay: `${i * 0.6}s`,
                      width: `${(i + 1) * 120}px`,
                      height: `${(i + 1) * 120}px`
                    }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Center content */}
            <motion.div
              className="relative z-10 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {/* Icon container with vibrant glow */}
              <div className="w-28 h-28 mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/30 to-primary/40 rounded-full backdrop-blur-md border border-brand-400/30 shadow-2xl shadow-brand-500/50"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="search-pulse"></div>
                  <Hotel className="w-14 h-14 text-brand-400 relative z-10 palm-sway" />
                </div>
              </div>
              
              <h3 className="text-xl font-light text-white mb-3 tracking-tight">Finding perfect stays</h3>
              <p className="text-white/70 max-w-md mx-auto text-sm font-normal leading-relaxed">
                Searching hotels in your destination
              </p>
              
              {/* Progress indicator */}
              <div className="mt-6 flex items-center justify-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse"></div>
                <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </motion.div>

            <style>{`
              /* Floating orbs with vibrant gradient */
              .orbs-container { 
                position: absolute; 
                inset: 0; 
                pointer-events: none; 
              }
              .floating-orb {
                position: absolute;
                border-radius: 50%;
                background: radial-gradient(circle at 30% 30%, rgba(32, 66, 238, 0.4), rgba(21, 20, 210, 0.3), transparent);
                filter: blur(60px);
                animation: float-orb 6s ease-in-out infinite;
              }
              .orb-1 { width: 400px; height: 400px; left: 10%; top: 20%; animation-delay: 0s; }
              .orb-2 { width: 350px; height: 350px; right: 10%; top: 30%; animation-delay: -2s; }
              .orb-3 { width: 300px; height: 300px; left: 50%; bottom: 20%; animation-delay: -4s; }
              .orb-4 { width: 280px; height: 280px; right: 30%; bottom: 25%; animation-delay: -1.5s; }
              
              @keyframes float-orb {
                0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
                25% { transform: translate(-40px, -30px) scale(1.15); opacity: 0.7; }
                50% { transform: translate(30px, 40px) scale(0.9); opacity: 0.6; }
                75% { transform: translate(-20px, 20px) scale(1.1); opacity: 0.65; }
              }
              
              /* Elegant rotating rings */
              .search-ring {
                position: absolute;
                border: 1px solid rgba(32, 66, 238, 0.2);
                border-radius: 50%;
                animation: rotate-ring 4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
              }
              
              @keyframes rotate-ring {
                0% { transform: rotate(0deg) scale(0.8); opacity: 0; }
                50% { opacity: 0.4; }
                100% { transform: rotate(180deg) scale(1.2); opacity: 0; }
              }
              
              /* Pulsing effect */
              .search-pulse {
                position: absolute;
                inset: -20%;
                border-radius: 50%;
                background: radial-gradient(circle, rgba(32, 66, 238, 0.3), transparent 70%);
                animation: pulse-glow 2s ease-in-out infinite;
              }
              
              @keyframes pulse-glow {
                0%, 100% { transform: scale(0.9); opacity: 0.5; }
                50% { transform: scale(1.1); opacity: 0.8; }
              }
              
              /* Palm tree gentle sway animation */
              .palm-sway {
                animation: palm-sway 3s ease-in-out infinite;
                transform-origin: center bottom;
              }
              
              @keyframes palm-sway {
                0%, 100% { transform: rotate(-3deg); }
                50% { transform: rotate(3deg); }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>

      {!isLoading && (
        <div className="w-full h-screen flex flex-col md:flex-row overflow-hidden relative">
          {/* Hotel List - Left Side (or Full Screen on mobile when map is hidden) */}
          <div 
            className={`${showMapView ? 'hidden md:block' : 'block'} md:w-1/2 overflow-y-auto px-6 md:px-8 pt-4 pb-8`} 
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 120px)' }}
            ref={hotelListRef}
          >
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="text-white/60 hover:text-white hover:bg-white/5 mb-2 -mt-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-light text-white mb-2 tracking-tight">
                  Select your stay
                </h2>
                <p className="text-white/60 text-sm md:text-base">
                  {allHotels.length > 0 && `${allHotels.length} hotel${allHotels.length !== 1 ? 's' : ''} found`}
                </p>
              </div>
              
              <div className="flex gap-2">
                {/* Map View Toggle (Mobile Only) */}
                <Button
                  onClick={() => setShowMapView(!showMapView)}
                  className="md:hidden bg-black border border-white/20 text-white hover:bg-white/10 gap-2"
                >
                  <MapIcon className="w-4 h-4" />
                  {showMapView ? 'List View' : 'Map View'}
                </Button>

                {/* Sort Controls */}
                {allHotels.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        className="bg-black border border-white/20 text-white hover:bg-black/80 gap-2"
                      >
                        <SlidersHorizontal className="w-4 h-4" />
                        Sort: {sortBy === 'price_low' ? 'Low to High' : sortBy === 'price_high' ? 'High to Low' : 'Rating'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem 
                        onClick={() => handleSortChange('price_high')}
                        className="cursor-pointer"
                      >
                        High to Low
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleSortChange('price_low')}
                        className="cursor-pointer"
                      >
                        Low to High
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleSortChange('rating')}
                        className="cursor-pointer"
                      >
                        Rating
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* Search Bar */}
            {allHotels.length > 0 && (
              <div className="relative mt-4">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search hotels by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 focus:border-white/40"
                />
              </div>
            )}
          </div>

          {/* Hotels List */}
          {hotels.length > 0 ? (
            <>
              <div className="space-y-4">
                {hotels.map((hotel, index) => (
                  <motion.div
                    key={getHotelId(hotel) || index}
                    data-hotel-id={getHotelId(hotel)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <StayOfferCard
                      hotel={hotel}
                      onHotelSelect={handleHotelSelect}
                      isSelected={getHotelId(selectedHotel) === getHotelId(hotel)}
                      flightData={flightData}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8">
                  <Button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="bg-black border-white/20 text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-white/60 text-sm">
                    Page {currentPage} of {totalPages} • {filteredCount} hotels
                  </span>
                  
                  <Button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="bg-black border-white/20 text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/85 text-lg">
                {searchQuery.trim() ? 'No hotels match your search' : 'No hotels found'}
              </p>
              <p className="text-white/60 text-sm mt-2">
                {searchQuery.trim() ? 'Try a different search term' : 'Please try a different destination or dates'}
              </p>
            </div>
          )}
        </div>

        {/* Map View - Right Side (or Full Screen on mobile when toggled) */}
        <div className={`${showMapView ? 'block' : 'hidden md:block'} md:w-1/2 h-[85vh] md:h-screen relative overflow-hidden`}>
          {cityCenter && allHotels.length > 0 ? (
            <MapContainer
              key={showMapView ? 'map-mobile-view' : 'map-desktop-view'}
              center={[cityCenter.lat, cityCenter.lng]}
              zoom={13}
              className="h-full w-full"
              style={{ background: '#000', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              zoomControl={true}
            >
              <DarkBasemap />
              {allHotels.map((hotel, index) => {
                // Location data is stored directly on hotel object as latitude/longitude
                if (!hotel.latitude || !hotel.longitude) {
                  return null;
                }
                
                const hotelId = getHotelId(hotel);
                const isSelected = getHotelId(selectedHotel) === hotelId;
                const rating = resolveHotelRating(hotel);
                // Round up the price (e.g., $313.77 -> $314)
                const roundedPrice = hotel.total_amount ? Math.ceil(hotel.total_amount) : 0;
                const priceText = roundedPrice ? `$${roundedPrice}` : '—';
                
                return (
                  <Marker
                    key={hotelId || index}
                    position={[hotel.latitude, hotel.longitude]}
                    icon={L.divIcon({
                      className: 'custom-marker',
                      html: `
                        <div>
                          <div class="hotel-marker-pin ${isSelected ? 'selected' : 'unselected'}">
                            ${priceText}
                          </div>
                        </div>
                      `,
                      iconSize: [48, 32],
                      iconAnchor: [24, 16]
                    })}
                    eventHandlers={{
                      click: () => {
                        // Scroll to hotel in list
                        const hotelCards = hotelListRef.current?.querySelectorAll('[data-hotel-id]');
                        const targetCard = Array.from(hotelCards || []).find(
                          card => card.getAttribute('data-hotel-id') === hotelId
                        );
                        if (targetCard) {
                          targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }
                    }}
                  >
                    <Popup className="dark-popup">
                      <div 
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { data: hotelData } = await fetchRoomRates(hotel.search_result_id);
                            setPopupHotelWithRates(hotelData);
                            setShowRoomRatesModal(true);
                          } catch (error) {
                            console.error('Failed to fetch room rates:', error);
                            toast.error('Failed to load room options');
                          }
                        }}
                        className="bg-black/95 text-white rounded-xl overflow-hidden cursor-pointer hover:bg-black transition-all w-[240px]"
                      >
                        {/* Hotel Image */}
                        {hotel.thumbnail && (
                          <div className="w-full h-32 overflow-hidden">
                            <img
                              src={hotel.thumbnail}
                              alt={hotel.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}
                        
                        {/* Hotel Details */}
                        <div className="p-3">
                          <h3 className="font-medium text-sm mb-2 line-clamp-1">{hotel.name}</h3>
                          
                          {/* Rating */}
                          {rating && (
                            <div className="flex items-center gap-1 mb-2">
                              <Star className="w-3 h-3 text-brand-400 fill-brand-400" />
                              <span className="text-xs text-white/80">{rating.label}</span>
                              {hotel.reviews > 0 && (
                                <span className="text-xs text-white/50">({hotel.reviews})</span>
                              )}
                            </div>
                          )}
                          
                          {/* Price */}
                          <div className="flex items-baseline gap-1 mb-2">
                            <span className="text-lg font-semibold text-white">
                              {hotel.total_amount ? formatPrice(hotel.total_amount) : '—'}
                            </span>
                            {hotel.nights && (
                              <span className="text-xs text-white/50">/ {hotel.nights} {hotel.nights === 1 ? 'night' : 'nights'}</span>
                            )}
                          </div>
                          
                          {/* Address */}
                          {hotel.address && (
                            <div className="flex items-start gap-1 text-xs text-white/60">
                              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{hotel.address.full || hotel.address.city}</span>
                            </div>
                          )}
                          
                          {/* Click hint */}
                          <div className="text-xs text-brand-400 mt-2">Click to view rooms</div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          ) : (
            <div className="h-full w-full bg-black flex items-center justify-center">
              <div className="text-white/60">
                <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Map will appear when hotels are loaded</p>
              </div>
            </div>
          )}

          {/* Mobile Map Toggle Button (on map view) */}
          {showMapView && (
            <Button
              onClick={() => setShowMapView(false)}
              className="md:hidden absolute top-4 right-4 z-[1000] bg-black border border-white/20 text-white hover:bg-white/10 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to List
            </Button>
          )}
        </div>

        {/* Fixed Continue to Review Bar - Only show when hotel is selected */}
        {selectedHotel && (
          <div 
            className="fixed inset-x-0 bottom-0 z-[70] md:left-0 md:right-auto md:w-1/2"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
          >
            <div className="bg-black/90 backdrop-blur-xl border-t border-white/10 px-6 md:px-8 py-5 md:py-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="text-white space-y-0">
                  <div className="text-[10px] text-white/40 leading-tight">Selected hotel</div>
                  <div className="text-xs font-medium leading-tight">{selectedHotel.name}</div>
                  <div className="text-sm font-medium leading-tight">
                    {formatPrice(selectedHotel.selectedRate?.total_amount ?? selectedHotel.total_amount)}
                  </div>
                </div>
                <Button
                  onClick={handleContinue}
                  disabled={isSavingSelection}
                  className="bg-white text-black hover:bg-gray-100 transition-colors rounded-full px-8 h-12 font-medium"
                >
                  {isSavingSelection ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Continue to Review'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Room Rates Modal for Map Popup */}
      {showRoomRatesModal && popupHotelWithRates && (
        <RoomRatesModal
          isOpen={showRoomRatesModal}
          onClose={() => {
            setShowRoomRatesModal(false);
            setPopupHotelWithRates(null);
          }}
          hotel={popupHotelWithRates}
          onRateSelect={(selectedRate) => {
            handleHotelSelect(popupHotelWithRates, selectedRate);
            setShowRoomRatesModal(false);
            setPopupHotelWithRates(null);
          }}
        />
      )}

      {/* Dark mode popup styles for Leaflet */}
      <style>{`
        .leaflet-popup-content-wrapper {
          background: transparent !important;
          border: none !important;
          border-radius: 0.75rem !important;
          padding: 0 !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        }
        .leaflet-popup-tip {
          background: rgba(0, 0, 0, 0.95) !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
        }
        .leaflet-popup-close-button {
          color: white !important;
          font-size: 20px !important;
          padding: 4px 8px !important;
        }
        .leaflet-popup-close-button:hover {
          color: #fff !important;
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .custom-marker {
          background: transparent !important;
          border: none !important;
        }
        .custom-marker > div {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
        }
        .hotel-marker-pin {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 48px !important;
          height: 32px !important;
          border-radius: 16px !important;
          border: 2px solid #ffffff !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.5) !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          padding: 0 8px !important;
          cursor: pointer !important;
        }
        .hotel-marker-pin.selected {
          background-color: #ffffff !important;
          color: #000000 !important;
          border-color: #000000 !important;
        }
        .hotel-marker-pin.unselected {
          background-color: #000000 !important;
          color: #ffffff !important;
          border-color: #ffffff !important;
        }
      `}</style>
    </div>
  );
}
